import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { isEqual, pick } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import useBeforeUnload from 'services/hooks/useBeforeUnload';
import submissionService from 'services/submission';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { compareCanvasRubrics } from 'utils/helper';

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth } = useAuth();
    const {
        course,
        setAssignments,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
        currentAssignment,
        previewSubmissions,
        setPreviewSubmissions,
    } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [selectRubricDialog, setSelectRubricDialog] = useState({ open: false });
    const [createRubricDialog, setCreateRubricDialog] = useState({ open: false });
    const [createRubricAIDialog, setCreateRubricAIDialog] = useState({ open: false });
    const [editRubricDialog, setEditRubricDialog] = useState({ open: false, customDefaultForm: null });
    const [editBaselineDialog, setEditBaselineDialog] = useState({ open: false });
    const [uploadContentBankResourcesDialog, setUploadContentBankResourcesDialog] = useState({ open: false });
    const [editContentBankResourcesDialog, setEditContentBankResourcesDialog] = useState({ open: false });
    const [previewUploadDialog, setPreviewUploadDialog] = useState({ open: false, target: null });
    const [previewFeedbackDialog, setPreviewFeedbackDialog] = useState({ open: false, output: null });
    const [feedbackSettingsDialog, setFeedbackSettingsDialog] = useState({ open: false });
    const [inlineCommentsSettingsDialog, setInlineCommentsSettingsDialog] = useState({ open: false });
    const [gradingSettingsDialog, setGradingSettingsDialog] = useState({ open: false });
    const [previewGradingDialog, setPreviewGradingDialog] = useState({ open: false, output: null });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isFeedbackSettingsDefault = (form) => {
        if (form.structure === 'rubric') {
            return (
                form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
                form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
                form.tone === DEFAULT_FEEDBACK_TONE
            );
        }
        if (form.structure === 'system') {
            return (
                form.numSuggestions === DEFAULT_FEEDBACK_NUM_SUGGESTIONS &&
                form.suggestionLength === DEFAULT_FEEDBACK_SUGGESTION_LENGTH &&
                form.languageLevel === DEFAULT_FEEDBACK_LANGUAGE_LEVEL &&
                form.tone === DEFAULT_FEEDBACK_TONE
            );
        }
        return false;
    };

    const isInlineCommentsSettingsDefault = (form) => {
        return (
            form.maxCommentsPerPage === DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE &&
            form.commentLength === DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH &&
            form.languageLevel === DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL &&
            form.tone === DEFAULT_INLINE_COMMENTS_TONE
        );
    };

    const validateAll = useCallback(() => {
        let isValid = true;
        let newErrors = {};

        if (!form.title.trim()) {
            isValid = false;
            newErrors = { ...newErrors, title: true };
        }

        if (!form.description.trim()) {
            isValid = false;
            newErrors = { ...newErrors, description: true };
        }

        if (!form.rubric) {
            isValid = false;
            newErrors = { ...newErrors, rubric: true };
        }

        if (errors.feedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, feedbackSettings: errors.feedbackSettings };
        }

        if (errors.gradingSettings) {
            isValid = false;
            newErrors = { ...newErrors, gradingSettings: errors.gradingSettings };
        }

        setErrors(newErrors);

        return {
            isValid,
            errors: newErrors,
        };
    }, [errors.feedbackSettings, errors.gradingSettings, form?.description, form?.rubric, form?.title]);

    const shouldPreviewReset = useCallback((oldAssignment, newAssignment) => {
        if (oldAssignment.has_finished_preview) return false;
        return !isEqual(
            pick(oldAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_inline_comments_enabled',
                'ai_inline_comments_settings',
                'ai_inline_comments_context',
            ]),
            pick(newAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_inline_comments_enabled',
                'ai_inline_comments_settings',
                'ai_inline_comments_context',
            ])
        );
    }, []);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                history.push(`/assignments/${currentAssignment.id}`);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const updateAssignment = useCallback(
        async (updatingRubric, api_data) => {
            setHasClickedSubmit(true);
            let updated_rubric = form.rubric;
            if (updatingRubric) updated_rubric = updatingRubric;
            const { isValid, errors } = validateAll();
            if (!isValid) {
                return setDialog({
                    open: true,
                    message: t(
                        'dialog.incomplete-assignment-warning.title',
                        'Please complete all missing required fields before publishing.'
                    ),
                    description: (
                        <ul>
                            {errors.title && (
                                <li style={{ textAlign: 'left' }}>{t('assignment-title-label', 'Assignment title')}</li>
                            )}
                            {errors.description && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('assignment-description-label', 'Assignment description')}
                                </li>
                            )}
                            {errors.rubric && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.assignment-rubric', 'Assignment rubric')}
                                </li>
                            )}
                            {errors.feedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.feedback-settings', 'Feedback settings')}
                                </li>
                            )}
                            {errors.gradingSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                </li>
                            )}
                        </ul>
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }

            setIsSaving(true);
            const timestamp = new Date().getTime();
            const updatedAssignment = {
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: updated_rubric,
                baseline: form.baseline,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: false,
                has_seen_preview: currentAssignment.has_seen_preview,
                has_finished_preview: currentAssignment.has_finished_preview,
                preview_files: currentAssignment.preview_files || [],
            };
            if (form?.api_assignment_id) {
                updatedAssignment.api_assignment_id = form.api_assignment_id;

                let new_api_data = api_data || form.api_data || currentAssignment.api_data;
                new_api_data = { ...new_api_data, id: form.api_assignment_id };
                updatedAssignment.api_data = new_api_data;
            }
            if (shouldPreviewReset(currentAssignment, updatedAssignment)) {
                updatedAssignment.has_seen_preview = false;
                updatedAssignment.preview_files = updatedAssignment.preview_files.map((pf) => {
                    const previewSubmissionToDelete = previewSubmissions.find(
                        (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                    );
                    if (previewSubmissionToDelete) {
                        setPreviewSubmissions((current) => {
                            return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                        });
                        submissionService.deleteSubmission(previewSubmissionToDelete.id);
                    }
                    return {
                        ...pf,
                        is_preview_up_to_date: false,
                    };
                });
            }
            await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
            setAssignments((current) => {
                return current.map((a) => {
                    return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
                });
            });
            message.success(t('message.update-success', 'Your assignment was successfully updated.'));
            history.push(`/assignments/${currentAssignment.id}`);
        },
        [
            t,
            closeDialog,
            course.id,
            currentAssignment,
            form?.aiFeedbackEnabled,
            form?.aiGradingEnabled,
            form?.aiStudentFeedbackEnabled,
            form?.baseline,
            form?.citationFormat,
            form?.contentBankResources,
            form?.description,
            form?.feedbackSettings,
            form?.feedbackCustomContext,
            form?.gradingCustomContext,
            form?.gradingSettings,
            form?.rubric,
            form?.title,
            form?.api_assignment_id,
            form?.aiInlineCommentsEnabled,
            form?.inlineCommentsSettings,
            form?.inlineCommentsCustomContext,
            form?.api_data,
            history,
            isGradingFeatureDisabled,
            isFeedbackFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            previewSubmissions,
            setAssignments,
            setDialog,
            setPreviewSubmissions,
            shouldPreviewReset,
            validateAll,
        ]
    );

    const unpublishAssignment = useCallback(async () => {
        setIsSaving(true);
        const timestamp = new Date().getTime();
        const updatedAssignment = {
            modified_on: timestamp,
            course_id: course.id,
            title: form.title,
            description: form.description,
            rubric: form.rubric,
            baseline: form.baseline,
            citation_format: form.citationFormat,
            content_bank_resources: form.contentBankResources,
            ai_feedback_enabled: form.aiFeedbackEnabled,
            ai_feedback_settings: {
                structure: form.feedbackSettings.structure,
                num_suggestions: form.feedbackSettings.numSuggestions,
                suggestion_length: form.feedbackSettings.suggestionLength,
                language_level: form.feedbackSettings.languageLevel,
                tone: form.feedbackSettings.tone,
                templated: form.feedbackSettings.templated,
                custom_instructions: form.feedbackSettings.customInstructions,
            },
            ai_feedback_context: form.feedbackCustomContext,
            ai_grading_enabled: form.aiGradingEnabled,
            ai_grading_settings: form.gradingSettings,
            ai_grading_context: form.gradingCustomContext,
            ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
            ai_inline_comments_enabled: form.aiInlineCommentsEnabled,
            ai_inline_comments_settings: {
                max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                comment_length: form.inlineCommentsSettings.commentLength,
                language_level: form.inlineCommentsSettings.languageLevel,
                tone: form.inlineCommentsSettings.tone,
            },
            ai_inline_comments_context: form.inlineCommentsCustomContext,
            is_draft: true,
            has_seen_preview: currentAssignment.has_seen_preview,
            has_finished_preview: currentAssignment.has_finished_preview,
            preview_files: currentAssignment.preview_files,
        };
        if (shouldPreviewReset(currentAssignment, updatedAssignment)) {
            updatedAssignment.has_seen_preview = false;
            updatedAssignment.preview_files = updatedAssignment.preview_files.map((pf) => {
                const previewSubmissionToDelete = previewSubmissions.find(
                    (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                );
                if (previewSubmissionToDelete) {
                    setPreviewSubmissions((current) => {
                        return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                    });
                    submissionService.deleteSubmission(previewSubmissionToDelete.id);
                }
                return {
                    ...pf,
                    is_preview_up_to_date: false,
                };
            });
        }
        await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
        setAssignments((current) => {
            return current.map((a) => {
                return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
            });
        });
        message.success(t('message.update-success', 'Your assignment was successfully updated.'));
        history.push(`/assignments/${currentAssignment.id}`);
    }, [
        t,
        course.id,
        form?.title,
        form?.description,
        form?.rubric,
        form?.baseline,
        form?.citationFormat,
        form?.contentBankResources,
        form?.aiFeedbackEnabled,
        form?.feedbackSettings,
        form?.feedbackCustomContext,
        form?.aiGradingEnabled,
        form?.gradingSettings,
        form?.gradingCustomContext,
        form?.aiStudentFeedbackEnabled,
        form?.aiInlineCommentsEnabled,
        form?.inlineCommentsSettings,
        form?.inlineCommentsCustomContext,
        currentAssignment,
        shouldPreviewReset,
        setAssignments,
        message,
        history,
        previewSubmissions,
        setPreviewSubmissions,
    ]);

    const onClickSave = useCallback(
        async ({ key }) => {
            if (key === 'publish') {
                const { isValid, errors } = validateAll();
                if (isValid) {
                    setIsSaving(true);
                    if (form.api_assignment_id && process.env.REACT_APP_KIND === 'CANVAS') {
                        const course_id = auth.course_id;
                        const accessToken = localStorage.getItem('access_token');
                        const APIAssignment = await apiService.getAssignment({
                            course_id,
                            accessToken,
                            assignment_id: form.api_assignment_id,
                        });
                        const is_matching = compareCanvasRubrics(form.rubric, APIAssignment.rubric);
                        if (!is_matching) {
                            return setPushRubricDialog({
                                open: true,
                                onSave: ({ api_data, rubric }) => {
                                    updateAssignment(rubric, api_data);
                                },
                                form: { ...currentAssignment, ...form },
                            });
                        } else {
                            updateAssignment();
                        }
                    } else {
                        updateAssignment();
                    }
                } else {
                    return setDialog({
                        open: true,
                        message: t(
                            'dialog.incomplete-assignment-warning.title',
                            'Please complete all missing required fields before publishing.'
                        ),
                        description: (
                            <ul>
                                {errors.title && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('assignment-title-label', 'Assignment title')}
                                    </li>
                                )}
                                {errors.description && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-description',
                                            'Assignment description'
                                        )}
                                    </li>
                                )}
                                {errors.rubric && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-rubric',
                                            'Assignment rubric'
                                        )}
                                    </li>
                                )}
                                {errors.feedbackSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.feedback-settings',
                                            'Feedback settings'
                                        )}
                                    </li>
                                )}
                                {errors.gradingSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                    </li>
                                )}
                            </ul>
                        ),
                        note: '',
                        variant: 'destructive',
                        actionButtonText: t('common:controls.ok', 'Ok'),
                        cancelButtonText: t('common:controls.cancel', 'Cancel'),
                        onAction: () => closeDialog(),
                        onCancel: () => closeDialog(),
                        onClose: () => closeDialog(),
                    });
                }
            }
            if (key === 'save') {
                setIsSaving(true);
                if (form.api_assignment_id && process.env.REACT_APP_KIND === 'CANVAS') {
                    const course_id = auth.course_id;
                    const accessToken = localStorage.getItem('access_token');
                    const APIAssignment = await apiService.getAssignment({
                        course_id,
                        accessToken,
                        assignment_id: form.api_assignment_id,
                    });
                    const is_matching = compareCanvasRubrics(form.rubric, APIAssignment.rubric);
                    if (!is_matching && form.rubric) {
                        return setPushRubricDialog({
                            open: true,
                            onSave: ({ api_data, rubric }) => {
                                if (rubric) {
                                    unpublishAssignment(rubric, api_data);
                                }
                            },
                            form: { ...currentAssignment, ...form },
                        });
                    } else {
                        unpublishAssignment();
                    }
                } else {
                    unpublishAssignment();
                }
            }
        },
        [
            auth.course_id,
            form,
            closeDialog,
            currentAssignment,
            setDialog,
            setPushRubricDialog,
            t,
            unpublishAssignment,
            updateAssignment,
            validateAll,
        ]
    );

    useEffect(() => {
        if (hasClickedSubmit) {
            validateAll();
        }
    }, [hasClickedSubmit, validateAll]);

    useEffect(() => {
        if (currentAssignment) {
            const assignmentForm = {
                title: currentAssignment.title,
                description: currentAssignment.description,
                rubric: currentAssignment.rubric,
                baseline: currentAssignment.baseline,
                citationFormat: currentAssignment.citation_format ?? CITATION_SELECT_OPTIONS[0].key,
                contentBankResources: currentAssignment.content_bank_resources || [],
                aiFeedbackEnabled: currentAssignment.ai_feedback_enabled,
                feedbackSettings: currentAssignment.ai_feedback_settings
                    ? {
                          structure: currentAssignment.ai_feedback_settings.structure,
                          numSuggestions: currentAssignment.ai_feedback_settings.num_suggestions,
                          suggestionLength: currentAssignment.ai_feedback_settings.suggestion_length,
                          languageLevel: currentAssignment.ai_feedback_settings.language_level,
                          tone: currentAssignment.ai_feedback_settings.tone,
                          templated: currentAssignment.ai_feedback_settings.templated,
                          customInstructions: currentAssignment.ai_feedback_settings.custom_instructions,
                      }
                    : {
                          structure: DEFAULT_FEEDBACK_STRUCTURE,
                          numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                          suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                          languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                          tone: DEFAULT_FEEDBACK_TONE,
                          templated: {},
                          customInstructions: '',
                      },
                feedbackCustomContext: currentAssignment.ai_feedback_context,
                aiGradingEnabled: currentAssignment.ai_grading_enabled,
                gradingSettings: currentAssignment.ai_grading_settings,
                gradingCustomContext: currentAssignment.ai_grading_context,
                aiStudentFeedbackEnabled: Boolean(currentAssignment.ai_student_feedback_enabled),
                aiInlineCommentsEnabled: Boolean(currentAssignment.ai_inline_comments_enabled),
                inlineCommentsSettings: currentAssignment.ai_inline_comments_settings
                    ? {
                          maxCommentsPerPage: currentAssignment.ai_inline_comments_settings.max_comments_per_page,
                          commentLength: currentAssignment.ai_inline_comments_settings.comment_length,
                          languageLevel: currentAssignment.ai_inline_comments_settings.language_level,
                          tone: currentAssignment.ai_inline_comments_settings.tone,
                      }
                    : {
                          maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
                          commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
                          languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
                          tone: DEFAULT_INLINE_COMMENTS_TONE,
                      },
                inlineCommentsCustomContext: currentAssignment.ai_inline_comments_context || '',
            };
            if (currentAssignment.api_assignment_id) {
                assignmentForm.api_assignment_id = currentAssignment.api_assignment_id;
            }
            setForm(assignmentForm);
        }
    }, [currentAssignment]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                selectRubricDialog,
                setSelectRubricDialog,
                createRubricDialog,
                setCreateRubricDialog,
                createRubricAIDialog,
                setCreateRubricAIDialog,
                editRubricDialog,
                setEditRubricDialog,
                editBaselineDialog,
                setEditBaselineDialog,
                uploadContentBankResourcesDialog,
                setUploadContentBankResourcesDialog,
                editContentBankResourcesDialog,
                setEditContentBankResourcesDialog,
                previewUploadDialog,
                setPreviewUploadDialog,
                previewFeedbackDialog,
                setPreviewFeedbackDialog,
                feedbackSettingsDialog,
                setFeedbackSettingsDialog,
                inlineCommentsSettingsDialog,
                setInlineCommentsSettingsDialog,
                gradingSettingsDialog,
                setGradingSettingsDialog,
                previewGradingDialog,
                setPreviewGradingDialog,
                pushRubricDialog,
                setPushRubricDialog,
                isSaving,
                setIsSaving,
                isFeedbackSettingsDefault,
                isInlineCommentsSettingsDefault,
                onClickBack,
                onClickSave,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
